import useProductCardV2View from '@components/Product/components/ProductCardV2/useProductCardV2View';
import { useEffect, useState } from 'react';
import { mediaSorterAsc } from '../../../../../../lib/MediasLib';
import useOrderBookStore from '../../../../../../stores/OrderBook/orderBook.store';
import useOrderBook from '../../../../../order-book/hooks/useOrderBook';
import { calculateProductTotalParts } from '../../../../../orders/products/orderProducts.utils';
import { useProductGrid } from '../../../../../orders/products/product/grid/components/orderProductGrid/hooks/useProductGrid/useProductGrid';
import { useProductGridActions } from '../../../../../orders/products/product/grid/hooks/useProductGridActions';
import { useGridStoreApi } from '../../../../../orders/products/product/grid/store/GridStoreProvider';
import { AvailabilityEnum } from '../../../../../products/ProductTypes';
import { IGridStatus } from '../../../../../products/products-details-v2/hooks/useCombination';
import { getButtonStatus } from '../../../../../products/products-details-v2/hooks/useCombination.utils';
import { useProductLock } from '../../../../../products/products-details-v2/hooks/useProductLock';
import useSharedEntities from '../../../../../shared-entities/hooks/useSharedEntities';
import { useAbility } from '../../../../../users/AbilityContext';
import { CatalogSectionProductColorV2 } from '../../../interfaces/catalogProductList.types';
import { RowCard } from '../../catalogProductsListV2.types';
import { buildProductCardProps } from '../../components/catalogProductRowV2.utils';
import { useCatalogMassActionsContext } from '../../contexts/CatalogMassActions.context';
import { useCatalogListContextParams } from '../../hooks/useCatalogListContextParams';
import { ToCatalogGridContainerPropsParams } from '../utils/catalogGridContainerProps.transformer';
import {
  CatalogListCombination,
  buildCatalogListCombinations,
} from '../utils/catalogListCombinations.utils';
import { buildCatalogListDisplayedGrid } from '../utils/catalogListDisplayedGrid.utils';
import { buildCatalogListRowProduct } from '../utils/catalogListRowProduct.utils';
import { buildCatalogListRowSelection } from '../utils/catalogListRowSelection.utils';
import {
  CatalogListPacksStatus,
  resolveGridButtonStatus,
  resolvePacksButtonStatus,
} from './catalogListAddOrderButton.utils';
import { CatalogListAddOrderButtonProps } from './useCatalogListAddOrderButton';
import useCatalogListCombinationOverflow from './useCatalogListCombinationOverflow';
import useCatalogListRowCopyPaste from './useCatalogListRowCopyPaste';

interface UseCatalogProductListRowParams {
  catalogId: string;
  card: Extract<RowCard, { kind: 'item' }>;
  productColor: CatalogSectionProductColorV2;
  isStacked: boolean;
}

const useCatalogProductListRow = ({
  catalogId,
  card,
  productColor,
  isStacked,
}: UseCatalogProductListRowParams) => {
  const { orderId } = useCatalogListContextParams();
  const { openOrderBook } = useOrderBook();
  const selectedOrderId = useOrderBookStore((state) => state.selectedOrder?.id);
  const massActions = useCatalogMassActionsContext();
  const { isPublicAccess, disableProductDetails } = useSharedEntities({});
  const ability = useAbility();
  const lock = useProductLock();
  const gridStoreApi = useGridStoreApi();

  const productCardProps = buildProductCardProps(card, catalogId);
  const view = useProductCardV2View(productCardProps);

  const combinations = buildCatalogListCombinations({
    availability: productColor.availability,
    productColorId: productColor.productColorId,
    deliveryWindowsByAvailability: productColor.deliveryWindowsByAvailability,
    orderId,
  });

  const [selectedCombinationKey, setSelectedCombinationKey] = useState<string | null>(null);

  const selectedCombination: CatalogListCombination | undefined =
    combinations.find((combination) => combination.combinationKey === selectedCombinationKey) ??
    combinations[0];

  const combinationKey = selectedCombination?.combinationKey;

  const productGrid = useProductGrid({
    productColorId: productColor.productColorId,
    availability: selectedCombination?.availability ?? AvailabilityEnum.EMPTY,
    deliveryWindowId: selectedCombination?.deliveryWindow?.id ?? null,
    orderId,
    useDefaultPriceTableWhenOffline: true,
    enabled: !!selectedCombination,
  });

  const displayedGrid = buildCatalogListDisplayedGrid({
    sizes: productColor.sizes ?? [],
    grid: productGrid.grid,
  });

  const combinationStrip = useCatalogListCombinationOverflow({
    combinations,
    selectedKey: selectedCombination?.combinationKey ?? '',
    isStacked,
  });

  const { handleSelectPacks } = useProductGridActions({
    grid: productGrid.grid,
    availability: selectedCombination?.availability ?? AvailabilityEnum.EMPTY,
    deliveryWindowId: selectedCombination?.deliveryWindow?.id ?? null,
    orderId,
    productColorId: productColor.productColorId,
    block: { isBlocked: !orderId || !selectedOrderId, onBlocked: openOrderBook },
  });

  const [packsStatus, setPacksStatus] = useState<CatalogListPacksStatus | null>(null);

  const gridButtonStatus = resolveGridButtonStatus({
    packsStatus,
    combinationKey,
    fallback: getButtonStatus({ hasEmptyAvailability: false, grid: productGrid.grid }),
  });

  const onChangeGridButtonStatus = (status: IGridStatus) => {
    if (!combinationKey) {
      return;
    }

    setPacksStatus({ combinationKey, status });
  };

  useEffect(() => {
    if (!combinationKey) {
      return;
    }

    gridStoreApi.getState().seed({ combinationKey, grid: displayedGrid });
  }, [gridStoreApi, combinationKey, displayedGrid]);

  const product = buildCatalogListRowProduct({
    productColor,
    orderId,
    combination: selectedCombination,
  });

  const copy = useCatalogListRowCopyPaste({
    product,
    orderId,
    selectedOrderId,
    grid: displayedGrid,
    resetSelectedPacks: productGrid.resetSelectedPacks,
    onNoSelectedOrder: openOrderBook,
  });

  const [firstImage] = (productColor.medias ?? [])
    .filter((media) => media.type === 'IMAGE')
    .sort(mediaSorterAsc);

  const canEditOrders = ability.can('update', 'orders');

  const onOpenDetails = (openedProductColorId?: string) => {
    if (openedProductColorId) {
      view.onColorClick(openedProductColorId);
      return;
    }

    view.onCardClick();
  };

  const gridParams: Omit<ToCatalogGridContainerPropsParams, 'addButton'> | undefined =
    selectedCombination && {
      product,
      combination: selectedCombination,
      productGrid,
      grid: displayedGrid,
      handleSelectPacks,
      lock,
      order: { orderId, selectedOrderId },
      surface: {
        isPublic: isPublicAccess,
        isStacked,
        canEditOrders,
        canAddToOrder: card.canAddToOrder,
      },
      gridStoreApi,
      copy,
      selloutMetrics: card.selloutMetrics,
      onNoSelectedOrder: openOrderBook,
      onPacksSelected: ({ totalQuantity }) =>
        onChangeGridButtonStatus(resolvePacksButtonStatus({ totalQuantity })),
    };

  const addOrderButtonProps: CatalogListAddOrderButtonProps | undefined = selectedCombination && {
    product: { productId: productColor.productId, productColorId: productColor.productColorId },
    orderId,
    combination: {
      combinationKey: selectedCombination.combinationKey,
      availability: selectedCombination.availability,
      deliveryWindowId: selectedCombination.deliveryWindow?.id,
    },
    productGrid,
    surface: { isPublic: isPublicAccess, canEditOrders, canAddToOrder: card.canAddToOrder },
    status: { value: gridButtonStatus, onChange: onChangeGridButtonStatus },
    onNoSelectedOrder: openOrderBook,
  };

  return {
    leftBlock: {
      name: productCardProps.item.name,
      code: productCardProps.item.code,
      referenceCode: productCardProps.item.referenceCode,
      entityRelations: productCardProps.item.entityRelations,
      tags: view.tags,
      imageUrl: firstImage?.url,
      colors: {
        allColors: productCardProps.item.allColors,
        selectedColor: productCardProps.item.selectedColor,
      },
      onOpenDetails: disableProductDetails ? undefined : onOpenDetails,
    },
    price: {
      ...view.price,
      grid: productGrid.grid,
      partsQuantity: calculateProductTotalParts(productGrid.grid),
      showTotal: !!orderId,
      hidePricesTooltip: disableProductDetails,
    },
    combinationStrip,
    selectedCombinationKey: combinationKey,
    hasGrid: !!selectedCombination && displayedGrid.length > 0,
    gridParams,
    addOrderButtonProps,
    selection: buildCatalogListRowSelection({
      massActions,
      card,
      productName: productColor.name,
    }),
    gridKey: orderId ?? 'no-order',
    onSelectCombination: setSelectedCombinationKey,
  };
};

export default useCatalogProductListRow;
